import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { viewDocumentAction } from "../../actions/viewDocumentAction";
import DocumentViewerModal from "./index";

/**
 * Hook for inline document preview with optional new-tab fullscreen.
 *
 * @param {number|string} entityPrimaryId
 * @returns {{
 *   viewDocument: (filePath: string, options?: { displayName?: string, onSuccess?: (filePath: string) => void }) => Promise<void>,
 *   closeViewer: () => void,
 *   DocumentViewer: React.FC,
 *   isViewerOpen: boolean,
 *   isLoading: boolean,
 * }}
 */
export const useDocumentViewer = (entityPrimaryId) => {
  const [isOpen, setIsOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [contentType, setContentType] = useState("");
  const [fileName, setFileName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const closeViewer = useCallback(() => {
    setUrl((prev) => {
      if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev);
      return "";
    });
    setIsOpen(false);
    setContentType("");
    setFileName("");
  }, []);

  const viewDocument = useCallback(
    async (filePath, options = {}) => {
      const { displayName, onSuccess } = options;
      if (!filePath) return;

      setIsLoading(true);
      const result = await viewDocumentAction(entityPrimaryId || 0, filePath);
      setIsLoading(false);

      if (result.success) {
        setUrl(result.url);
        setContentType(result.contentType);
        setFileName(displayName || filePath.replace(/^.*[/\\]/, ""));
        setIsOpen(true);
        onSuccess?.(filePath);
      } else {
        toast.error(result.error || "Failed to load document");
      }
    },
    [entityPrimaryId],
  );

  const DocumentViewer = () => (
    <DocumentViewerModal
      isOpen={isOpen}
      onClose={closeViewer}
      url={url}
      contentType={contentType}
      fileName={fileName}
    />
  );

  return {
    viewDocument,
    closeViewer,
    DocumentViewer,
    isViewerOpen: isOpen,
    isLoading,
  };
};
